import type { Subscription } from 'rxjs'
import {
  EMPTY,
  Observable,
  ReplaySubject,
  TimeoutError,
  catchError,
  defer,
  filter,
  finalize,
  forkJoin,
  from,
  map,
  mergeMap,
  of,
  race,
  share,
  switchMap,
  take,
  tap,
  throwError,
  timeout,
  timer
} from 'rxjs'
import type { HttpResponse } from '@angular/common/http'
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import type { OnDestroy } from '@angular/core'
import { effect, Injectable, inject } from '@angular/core'
import {
  apiEndpoint,
  apiProtocol,
  app,
  clientSecret,
  environment,
  isLiveMobile,
  mobileAppClientCredentials
} from '@awork/environments/environment'
import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import { ApiCacheService } from '@awork/_shared/services/api-cache-service/api-cache.service'
import { BrowserService } from '@awork/_shared/services//browser-service/browser.service'
import type { User } from '@awork/features/user/models/user.model'
import type { Subdomain, Workspace } from '@awork/features/workspace/models/workspace.model'
import { AccountStore } from '@awork/_shared/state/account.store'
import { AccountQuery } from '@awork/_shared/state/account.query'
import { encrypt } from '@awork/_shared/functions/encrypt'
import { get as getCookie, remove as removeCookie, set as setCookie } from 'es-cookie'
import { addMonths } from '@awork/_shared/functions/date-fns-wrappers'
import { buildQueryString } from '@awork/_shared/functions/query-params-helper'
import { AppStore } from '@awork/core/state/app.store'
import { AppQuery } from '@awork/core/state/app.query'
import { DEFAULT_PERSIST_DELAY } from '@awork/core/state/signal-store/types'
import { UserQuery } from '@awork/features/user/state/user.query'
import type { OAuthResponse } from '@awork/_shared/models/oauth.model'
import { generateGUID } from '@awork/_shared/functions/guid-generator'
import { WorkspaceStore } from '@awork/features/workspace/state/workspace.store'
import { WorkspaceQuery } from '@awork/features/workspace/state/workspace.query'
import type { AccountState } from '@awork/_shared/models/account.model'
import { Account } from '@awork/_shared/models/account.model'
import type { ValidateAccount, ValidatedAccount } from '@awork/_shared/models/validate-account.model'
import { TrackingService } from '@awork/_shared/services/tracking-service/tracking.service'
import { authentication } from '@microsoft/teams-js'
import { AutoUnsubscribe } from '@awork/_shared/decorators/auto-unsubscribe'
import { setPasskeyLoginPreference } from '@awork/_shared/functions/passkey-login-preference'
import localStorageHelper from '@awork/_shared/functions/local-storage'
import { LogService } from '@awork/_shared/services/log-service/log.service'
import { Level } from '@awork/_shared/services/log-service/types'
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject'
import getHostname from '@awork/_shared/functions/get-hostname'
import { getPortlessWorkspaceOrigin } from '@awork/_shared/functions/portless-hostname'
import { rotateCharacters } from '@awork/_shared/functions/string-operations'
import { SignalStoreService } from '@awork/core/state/signal-store/signalStore.service'
import type {
  AuthResponse,
  AuthorizedClientApplication,
  AuthorizedClientApplicationRevocationResponse,
  MfaConfiguration
} from '@awork/_shared/services/account-service/types'
import { LoginData, MfaProvider } from '@awork/_shared/services/account-service/types'
import { guidRegexString } from '@awork/_shared/static-data/regular-expressions'
import { ProductCapabilities } from '../product/product-capabilities.service'

export const levelMap = {
  INFO: Level.info,
  WARN: Level.warn,
  ERROR: Level.error,
  FATAL: Level.fatal
}

const DESKTOP_CLIENT_COOKIE_NAME = 'aw_auth_client'
const LOGIN_IMAGE_TIMEOUT_MS = 3000

/**
 * Builds the workspace login URL for a rejected login method.
 * @param {string} errorCode Login error code.
 * @param {string} subdomain Workspace subdomain.
 * @param {string} hostname App hostname.
 * @param {Location} browserLocation Current browser location.
 * @returns {string} The workspace login URL.
 */
export function getForbiddenLoginMethodUrl(
  errorCode: string,
  subdomain: string,
  hostname: string,
  browserLocation: Location = window.location
): string {
  const workspaceOrigin = getPortlessWorkspaceOrigin(subdomain, browserLocation) ?? `https://${subdomain}.${hostname}`
  return `${workspaceOrigin}/login?error=${errorCode}`
}

@AutoUnsubscribe()
@Injectable({ providedIn: 'root' })
export class AccountService implements OnDestroy {
  private readonly product = inject(ProductCapabilities)
  private readonly homeRoute = this.product.homeRoute
  private apiClient = inject(ApiClient)
  private apiCacheService = inject(ApiCacheService)
  private _router = inject(Router)
  private _route = inject(ActivatedRoute)
  private browserService = inject(BrowserService)
  private accountStore = inject(AccountStore)
  private accountQuery = inject(AccountQuery)
  private appStore = inject(AppStore)
  private appQuery = inject(AppQuery)
  private userQuery = inject(UserQuery)
  private workspaceStore = inject(WorkspaceStore)
  private workspaceQuery = inject(WorkspaceQuery)
  private trackingService = inject(TrackingService)
  private logService = inject(LogService)
  private signalStoreService = inject(SignalStoreService)

  private readonly url: string
  private readonly hostname: string
  private redirectUrl?: string // Store the URL that is attempted to access
  private queryParam?: string
  public email: string
  public account: AccountState
  private password: string
  private loginEndpoint: string

  private refreshSessionRequest$: Observable<AuthResponse | void> | null = null
  private reloadAfterSessionRefresh = false
  refreshingSession$ = new BehaviorSubject<boolean>(false)

  subscriptions: Subscription[] = []

  subdomainCookie = 'aw_sd'

  accountFetched: boolean

  private clearStoreAfterNavigation = false

  private tempSavedCredentials: {
    email: string
    password: string
  }

  constructor() {
    this.url = `${apiEndpoint}/accounts/`
    this.hostname = getHostname()

    // In local dev mode, get the access token instead of a cookie.
    this.loginEndpoint = isLiveMobile ? 'token' : 'cookie'

    this.listenLogoutNavigation()

    effect(() => {
      const accountState = this.accountQuery.query()
      this.appStore.update({ isLoggedIn: accountState()?.email !== null && !!accountState().refreshAt })
    })

    this.accountFetched = false
  }

  /**
   * Listens to the logout navigation and clears the store if the user is logged out
   */
  listenLogoutNavigation(): void {
    this._router.events
      .pipe(filter(event => event instanceof NavigationEnd && this.clearStoreAfterNavigation))
      .subscribe(() => {
        const isLoggedIn = this.appQuery.getIsLoggedIn()
        let route: ActivatedRoute | null = this._route

        while (route && route.snapshot.data?.module !== 'auth') {
          route = route.firstChild
        }

        const isInAuthModule = !!route

        if (!isLoggedIn && isInAuthModule) {
          this.clearStore()

          this.clearStoreAfterNavigation = false
        }
      })
  }

  /** Allows AutoUnsubscribe to clean up service subscriptions. */
  ngOnDestroy(): void {}

  /**
   * Sets the temporarily saved credentials
   * @param {{ email: string; password: string; }} credentials
   */
  set savedCredentials(credentials: { email: string; password: string }) {
    this.tempSavedCredentials = credentials
  }

  /**
   * Gets the temporarily saved credentials
   * @returns {{ email: string; password: string; }}
   */
  get savedCredentials(): { email: string; password: string } {
    return this.tempSavedCredentials
  }

  /**
   * Clears out the temporarily saved credentials
   */
  clearSavedCredentials(): void {
    this.tempSavedCredentials = null
  }

  /**
   * Updates the active account state.
   * @param {AccountState} account Account state to apply.
   */
  updateAccount(account: AccountState): void {
    this.accountStore.updateAccount(account)
  }

  /**
   * Makes an API call to sign-up and login the user via OAuth
   * @param {OAuthResponse} oauthResponse
   * @param {string} redirectURL
   * @param {string} queryParam
   * @returns {Observable<AuthResponse>}
   */
  socialConnect(oauthResponse: OAuthResponse, redirectURL?: string, queryParam?: string): Observable<AuthResponse> {
    const validateEmailDomain =
      oauthResponse.origin === 'signup' &&
      !oauthResponse.connectInviteCode &&
      oauthResponse.provider === 'google' &&
      !oauthResponse.fromMetaCampaign &&
      !oauthResponse.isInternalInvite

    const socialConnectBody = {
      provider: oauthResponse.provider,
      authorizationCode: oauthResponse.authorizationCode,
      idToken: oauthResponse.idToken,
      type: oauthResponse.origin === 'invitation' ? 'signup' : oauthResponse.origin,
      workspaceId: oauthResponse.workspaceId,
      invitationCode: oauthResponse.invitationCode,
      referralCode: oauthResponse.referralCode ? oauthResponse.referralCode : '',
      state: oauthResponse.state,
      validateEmailDomain,
      connectInviteCode: oauthResponse.connectInviteCode
    }

    return this.apiClient
      .post<{
        subdomain: string
        email: string
        workspaceId: string
        accessToken: string
        userId: string
      }>(
        `${apiEndpoint}/invitations/socialconnect`,
        socialConnectBody,
        oauthResponse.origin === 'signup'
          ? { headers: new HttpHeaders({ timezone: this.browserService.getTimezone() }) }
          : undefined
      )
      .pipe(
        mergeMap(resp => {
          if (queryParam) {
            this.queryParam = queryParam
          }

          if (oauthResponse.origin === 'signup') {
            return from(this.trackingService.trackSignUpStarted(resp.userId, resp.email)).pipe(
              switchMap(() => {
                return this.login(
                  resp.email,
                  generateGUID(),
                  resp.workspaceId,
                  { name: resp.subdomain, isDefault: true },
                  { provider: oauthResponse.provider, oauthAccessToken: resp.accessToken, userId: resp.userId }
                )
              })
            )
          }

          if (redirectURL) {
            this.redirectUrl = redirectURL
          }

          return this.login(
            resp.email,
            generateGUID(),
            resp.workspaceId,
            { name: resp.subdomain, isDefault: true },
            { provider: oauthResponse.provider, oauthAccessToken: resp.accessToken, userId: resp.userId }
          )
        }),
        catchError(error => this.onLoginError(error))
      )
  }

  /**
   * Makes an API call to add the OAuth integration to the account
   * @param {OAuthResponse} oauthResponse
   * @returns {Observable<void>}
   */
  socialConnectAccount(oauthResponse: OAuthResponse): Observable<void> {
    return this.apiClient.post<void>(`${this.url}connecttosocialaccount`, {
      code: oauthResponse.authorizationCode,
      provider: oauthResponse.provider
    })
  }

  /**
   * Makes an API call to remove the OAuth integration from the account
   * @param {string} provider
   * @returns {Observable<void>}
   */
  socialDisconnectAccount(provider: string): Observable<void> {
    return this.apiClient.post<void>(`${this.url}disconnecttosocialaccount`, { provider }).pipe(
      tap(() => {
        const account = this.accountQuery.getAccount()
        const filteredProviders = account.externalAccounts.filter(
          externalAccount => externalAccount.provider !== provider
        )

        switch (provider) {
          case 'google':
            this.accountStore.updateAccount({ googleUserId: null, externalAccounts: filteredProviders })
            break
          case 'apple':
            this.accountStore.updateAccount({ appleUserId: null, externalAccounts: filteredProviders })
        }
      })
    )
  }

  /**
   * Gets the OAuth client applications authorized by the current account.
   * @returns {Observable<AuthorizedClientApplication[]>}
   */
  getAuthorizedClientApplications(): Observable<AuthorizedClientApplication[]> {
    return this.apiClient.get<AuthorizedClientApplication[]>(`${this.url}me/authorizedclients`)
  }

  /**
   * Revokes refresh tokens and authorization grants for one OAuth client application.
   * @param {string} clientId The OAuth client id to revoke.
   * @returns {Observable<AuthorizedClientApplicationRevocationResponse>}
   */
  revokeAuthorizedClientApplication(clientId: string): Observable<AuthorizedClientApplicationRevocationResponse> {
    return this.apiClient.delete<AuthorizedClientApplicationRevocationResponse>(
      `${this.url}me/authorizedclients/${encodeURIComponent(clientId)}`
    )
  }

  /**
   * Revokes refresh tokens and authorization grants for all OAuth client applications.
   * @returns {Observable<AuthorizedClientApplicationRevocationResponse>}
   */
  revokeAllAuthorizedClientApplications(): Observable<AuthorizedClientApplicationRevocationResponse> {
    return this.apiClient.delete<AuthorizedClientApplicationRevocationResponse>(`${this.url}me/authorizedclients`)
  }

  /** MULTI-FACTOR AUTHENTICATION START */

  /**
   * Makes an API call to get multi-factor authentication configuration
   * @returns {Observable<MfaConfiguration>}
   */
  getMultiFactorAuthEnabled(): Observable<MfaConfiguration> {
    return this.apiClient.get<MfaConfiguration>(`${this.url}mfaconfiguration`)
  }

  /**
   * Makes an API call to get multi-factor authentication configuration
   * @param {boolean} enableMFA True to enable multi-factor authentication
   * @param {boolean} enableAuthenticator True to enable the authenticator application
   * @returns {Observable<MfaConfiguration>}
   */
  updateMultiFactorAuthEnabled(enableMFA: boolean, enableAuthenticator?: boolean): Observable<MfaConfiguration> {
    return this.apiClient.post<MfaConfiguration>(`${this.url}mfaconfiguration`, {
      enableMFA,
      enableAuthenticator
    })
  }

  /**
   * Makes an API call to validate the MFA token
   * @param {string} email The email of the user
   * @param {MfaProvider} provider The MFA provider to use for validation
   * @param {string} token The MFA token to validate
   * @returns {Observable<MfaConfiguration>}
   */
  validateMFAToken(email: string, provider: MfaProvider, token: string): Observable<MfaConfiguration> {
    return this.apiClient.post<MfaConfiguration>(`${this.url}ValidateMFAToken`, {
      email: Account.getImpersonatorEmail(email),
      provider,
      token
    })
  }

  /**
   * Makes an API call to send the MFA token via email
   * @param {string} email The email to send the MFA token to
   * @param {MfaProvider} provider The MFA provider to use for sending the token
   * @returns {Observable<MfaConfiguration>}
   */
  sendMFAToken(email: string, provider: MfaProvider): Observable<MfaConfiguration> {
    return this.apiClient.post<MfaConfiguration>(`${this.url}sendMFAToken`, {
      email: Account.getImpersonatorEmail(email),
      provider
    })
  }

  /** MULTI-FACTOR AUTHENTICATION END */

  /**
   * Makes an API call to request login
   * @param {string} email
   * @param {string} password
   * @param {string} workspaceId
   * @param {Subdomain} subdomain
   * @param {{ provider?: string; oauthAccessToken?: string; userId?: string; emailVerificationUrlParams?: string; mfaTokenProvider?: MfaProvider; mfaToken?: string; }} [options={}]
   *   Additional options to configure during login
   * @returns {Observable<AuthResponse>}
   * @param {string} options.provider
   * @param {string} options.oauthAccessToken
   * @param {string} options.userId
   * @param {string} options.emailVerificationUrlParams - all the parameters that we want to be provided to the email verification link
   * @param {MfaProvider} options.mfaTokenProvider - The MFA provider used for login
   * @param {string} options.mfaToken - The MFA token used for login
   */
  public login(
    email: string,
    password: string,
    workspaceId: string,
    subdomain: Subdomain,
    options: {
      provider?: string
      oauthAccessToken?: string
      userId?: string
      emailVerificationUrlParams?: string
      mfaTokenProvider?: MfaProvider
      mfaToken?: string
    } = {}
  ): Observable<AuthResponse> {
    const { provider, oauthAccessToken, userId } = options
    let url = ''

    if (environment !== 'local' && app === 'web' && subdomain) {
      url = `${apiProtocol}${subdomain.name}.${this.hostname}${this.url}${this.loginEndpoint}`
    } else {
      url = `${this.url}${this.loginEndpoint}`
    }

    this.email = email

    const authHeader = isLiveMobile ? { authorization: `Basic ${mobileAppClientCredentials}` } : {}
    const otherHeaders = oauthAccessToken ? { aw_connect: oauthAccessToken } : {}

    return this.apiClient
      .post<AuthResponse>(
        url,
        this.getBody({
          nonce: encrypt(clientSecret, JSON.stringify({ workspaceId, date: new Date() })),
          grant_type: isLiveMobile ? 'password' : null,
          scope: isLiveMobile ? 'offline_access full_access' : null,
          username: email,
          password,
          workspace_id: workspaceId,
          provider,
          url_params: options?.emailVerificationUrlParams,
          mfa_token_provider: options?.mfaTokenProvider,
          mfa_token: options?.mfaToken
        }),
        {
          headers: new HttpHeaders({
            ...authHeader,
            ...otherHeaders
          })
        }
      )
      .pipe(
        map(resp => {
          if (app === 'web' && this.browserService.getSubdomain() === 'app') {
            setPasskeyLoginPreference(resp.passwordlessEnabled)
          }

          resp.userId = userId
          resp.email = email

          return this.processLoginResponse(resp, email, subdomain)
        }),
        catchError(error => {
          const credentialsRequiredErrorCodes = ['email-not-verified', 'mfa-required']

          if (credentialsRequiredErrorCodes.includes(error?.error?.code) && app === 'web') {
            this.tempSavedCredentials = {
              email,
              password
            }
          }

          return this.onLoginError(error, subdomain, { isCodeExpired: !!options?.mfaToken })
        })
      )
  }

  /**
   * Processes the login response and redirects the user to the app
   * @param {AuthResponse} resp
   * @param {string} email
   * @param {Subdomain} subdomain
   * @returns {AuthResponse}
   */
  private processLoginResponse(resp: AuthResponse, email: string, subdomain: Subdomain): AuthResponse {
    this.apiCacheService.invalidate({ all: true })
    this.setSubdomainCookie(subdomain)

    this.clearSavedCredentials()

    resp.subdomain = subdomain

    // If the user attempted to access an internal page prior login, redirect to it after success login
    let params = location.search || '?'

    if (location.search && this.queryParam) {
      params += `&${this.queryParam}`
    }

    const redirect = this.redirectUrl || this.homeRoute + params

    const browserSubdomain = this.browserService.getSubdomain()

    // If the environment is not local and not a custom subdomain (www)
    // Redirect to the custom subdomain
    if (
      environment !== 'local' &&
      app === 'web' &&
      (!this.browserService.isCustomSubdomain() || browserSubdomain.toLowerCase() !== subdomain.name.toLowerCase())
    ) {
      // Encrypt object with email and token expiration date
      const encryptedParams = encrypt(
        clientSecret,
        JSON.stringify({
          email,
          expiresIn: resp.expires_in,
          redirect
        })
      )

      window.location.replace(
        `https://${subdomain.name}.${this.hostname}/login?d=${encodeURIComponent(encryptedParams)}`
      )
    } else {
      const account = new Account(email, resp.expires_in)

      this.accountStore.updateAccount({
        email,
        refreshToken: resp.refresh_token,
        accessToken: resp.access_token,
        refreshAt: account.refreshAt
      })

      this.appStore.update({ isLoggedIn: true })

      const isInMSTeams = this.appQuery.getIsInMSTeams()
      const isFirefoxOrEdge =
        navigator.userAgent.toLowerCase().indexOf('firefox') > -1 ||
        navigator.userAgent.toLowerCase().indexOf('edge') > -1

      if (isInMSTeams && !isFirefoxOrEdge) {
        // Wait until the store is set with the account information otherwise MS Teams will not have it
        forkJoin([
          this.signalStoreService.selectOnStorePersisted('account'),
          this.signalStoreService.selectOnStorePersisted('app')
        ]).subscribe(() => {
          authentication.notifySuccess('')
        })
        // Redirect only in web app
      } else if (app === 'web') {
        // Save the session before permission loading can reload the app.
        const sessionSaved$ = this.signalStoreService.getStorageStatus().enabled
          ? this.signalStoreService.selectOnStorePersisted('account')
          : of(null)

        sessionSaved$.pipe(take(1)).subscribe(() => this._router.navigateByUrl(redirect))
      }
    }

    return resp
  }

  /**
   * Check if there is a awr param inside the url.
   * Then redirect to the url in which is specified in
   * the awr paramter,
   * @param {string} url The current url
   * @returns {string}
   */
  public appsFlyerRedirect(url: string): string {
    if (url) {
      if (url.includes('awr=')) {
        // extract redirect url and
        const redirect = decodeURIComponent(url.split('awr=')[1])

        if (redirect && redirect.includes('awork.com')) {
          return redirect
        }
      }
    }

    return null
  }

  /**
   * Handles login call's errors
   * @param {HttpErrorResponse} error
   * @param {Subdomain} subdomain
   * @param {{ isCodeExpired?: boolean; }} options Additional options to configure during error handling
   * @returns {Observable<never>}
   * @param {boolean} options.isCodeExpired - True if the MFA code is expired
   * @private
   */
  private onLoginError(
    error: HttpErrorResponse,
    subdomain?: Subdomain,
    options?: { isCodeExpired?: boolean }
  ): Observable<never> {
    switch (error.error.code) {
      case 'login-precondition-failed':
        // Stay on the current auth page so callers can show feedback (missing/expired aw_lg cookie).
        break
      case 'workspace-product-access-denied':
        if (app === 'web') {
          this._router.navigate(['/product-access-denied'])
        }

        break
      case 'workspace-access-denied':
      case 'user-access-denied':
        this.removeSubdomainCookie()
        break
      case 'credential-login-forbidden':
      case 'social-login-forbidden':
        this.handleForbiddenLoginMethod(error.error.code, subdomain)
        break
      case 'email-not-verified':
        if (app === 'web') {
          this._router.navigate(['/verify-email'], {
            queryParams: { email: btoa(this.savedCredentials.email) },
            queryParamsHandling: 'merge'
          })
        }

        break
      case 'mfa-required': {
        const mfaProviders: MfaProvider[] = error.error.details
        const includeAuthenticator = mfaProviders.includes(MfaProvider.Authenticator)
        const isCodeExpired = options?.isCodeExpired

        const provider = includeAuthenticator && !isCodeExpired ? MfaProvider.Authenticator : MfaProvider.Email

        this._router.navigate(['/mfa-code'], {
          queryParams: {
            provider,
            includeAuthenticator,
            isCodeExpired: isCodeExpired || null
          },
          queryParamsHandling: 'merge'
        })
        break
      }
    }

    return throwError(error)
  }

  /**
   * API call to switch workspace
   * @param {string} workspaceId The workspace id
   * @param {Subdomain} subdomain The workspace subdomain
   * @returns {Observable<AuthResponse>} The new refresh token
   */
  public switchWorkspace(workspaceId: string, subdomain: Subdomain): Observable<AuthResponse> {
    let url: string
    const refreshEndpoint = this.getRefreshEndpoint()

    if (environment !== 'local' && app === 'web') {
      url = `${apiProtocol}${subdomain.name}.${this.hostname}${this.url}${refreshEndpoint}`
    } else {
      url = `${this.url}${refreshEndpoint}`
    }

    return this.apiClient
      .post<AuthResponse>(
        url,
        this.getBody({
          grant_type: isLiveMobile ? 'refresh_token' : null,
          nonce: encrypt(clientSecret, JSON.stringify({ workspaceId, date: new Date() })),
          workspace_id: workspaceId,
          refresh_token: this.accountQuery.getAccount().refreshToken
        }),
        {
          headers: new HttpHeaders(isLiveMobile ? { authorization: `Basic ${mobileAppClientCredentials}` } : {})
        }
      )
      .pipe(
        map(response => {
          const currentWorkspace = this.workspaceQuery.getCurrentWorkspace()

          if (app === 'mobile') {
            const accountState = this.accountQuery.getAccount()
            const email = accountState.email
            const refreshToken = accountState.refreshToken
            const account = new Account(email, response.expires_in)

            this.accountStore.updateAccount({
              email,
              refreshToken: response.refresh_token ? response.refresh_token : refreshToken,
              accessToken: response.access_token,
              refreshAt: account.refreshAt
            })

            // For mobile, this flag is used to trigger the synchronization
            if (currentWorkspace && currentWorkspace.id !== workspaceId) {
              this.appStore.update({ isSwitchingWorkspace: true })

              // Reset the flag after 2 sec
              setTimeout(() => this.appStore.update({ isSwitchingWorkspace: false }), 2000)
            }
          }

          if (currentWorkspace?.id !== workspaceId) {
            this.apiCacheService.invalidate({ all: true })
          }

          this.workspaceStore.setCurrentWorkspace(workspaceId)

          return response
        }),
        catchError((error: HttpErrorResponse) => {
          // Log only diagnostic fields. The full error contains the workspace URL and response body.
          this.logService.logMessage(
            Level.error,
            `Error refreshing token. Workspace: ${workspaceId}. Status: ${error?.status}.`,
            {
              status: error?.status,
              endpoint: refreshEndpoint,
              workspaceId,
              traceId: error?.headers?.get?.('trace-id') ?? undefined
            }
          )
          return throwError(error)
        })
      )
  }

  /**
   * Returns the refresh route for the OAuth client that issued this session.
   * @returns {string} The session refresh route.
   */
  private getRefreshEndpoint(): string {
    if (isLiveMobile) {
      return 'token'
    }

    const desktopClient = getCookie(DESKTOP_CLIENT_COOKIE_NAME)

    return desktopClient === 'aw-desktop' ? 'desktoprefreshcookie' : 'refreshcookie'
  }

  /**
   * Refreshes the browser with the first subdomain in the workspace
   * @param {Workspace} workspace The workspace selected by the user
   * @param {string} redirectUrl The URL to redirect after the workspace is switched
   */
  switchWorkspaceAndRedirect(workspace: Workspace, redirectUrl?: string): void {
    const account = this.accountQuery.getAccount()

    if (workspace && account) {
      const email = account.email
      const refreshToken = account.refreshToken
      const subdomain = workspace.getDefaultSubdomain(this)

      if (email) {
        this.switchWorkspace(workspace.id, subdomain).subscribe({
          next: res => {
            this.appStore.update({ isSwitchingWorkspace: true })

            // Encrypt object with email and token expiration date
            const encryptedParams = encrypt(
              clientSecret,
              JSON.stringify({
                email,
                expiresIn: res.expires_in,
                redirect: redirectUrl
              })
            )

            let newUrl = `https://${subdomain.name}.${this.hostname}/`

            if (environment === 'local') {
              newUrl = `${getPortlessWorkspaceOrigin(subdomain.name) ?? window.location.origin}/`
            }

            newUrl = newUrl.concat(`login?d=${encodeURIComponent(encryptedParams)}`)

            if (environment === 'local' && res.access_token && (res.refresh_token || refreshToken)) {
              newUrl = newUrl.concat(`&refresh_token=${res.refresh_token ? res.refresh_token : refreshToken}`)
              newUrl = newUrl.concat(`&access_token=${res.access_token}`)
              newUrl = newUrl.concat(`&subdomain=${subdomain.name}`)
            }

            this.setSubdomainCookie(subdomain)
            this.clearStoreAndWait()
              .pipe(
                take(1),
                catchError(error => {
                  this.logService.logMessage(Level.warn, 'Store cleanup failed before workspace switch redirect.', {
                    error
                  })
                  return of(false)
                })
              )
              .subscribe(() => window.location.replace(newUrl))
          },
          error: () => {
            this.apiClient.clientError$.next(q.translations.AccountService.workspaceSwitchFailed)
          }
        })
      }
    }
  }

  /**
   * Calculates the refresh time of the session based on the expiration of the current session
   * @returns {number} Time in milliseconds
   */
  getRefreshTime(): number {
    const account = this.accountQuery.getAccount()

    if (account && account.refreshAt) {
      try {
        const now = new Date()
        const refreshDate = new Date(account.refreshAt)
        const offset = 6000 // 1 minute
        const refreshTime = refreshDate.getTime() - now.getTime() - offset

        return refreshTime > 0 ? refreshTime : 1
      } catch (error) {
        return 1
      }
    }

    return null
  }

  /**
   * Shares an active session refresh and updates the account once.
   * @param {boolean} [reload=true] Reloads after success if any concurrent caller requests it.
   * @returns {Observable<AuthResponse | void>}
   */
  public refreshSession(reload = true): Observable<AuthResponse | void> {
    if (this.refreshSessionRequest$) {
      this.reloadAfterSessionRefresh ||= reload

      return this.refreshSessionRequest$
    }

    this.reloadAfterSessionRefresh = reload

    const request$: Observable<AuthResponse | void> = defer(() => {
      this.refreshingSession$.next(true)

      const currentUrl = window.location.href
      const workspaceReady$ = app === 'mobile' ? this.signalStoreService.selectOnStoreRestored('workspace') : of(void 0)

      return workspaceReady$.pipe(
        switchMap(() => {
          const workspace = this.workspaceQuery.getCurrentWorkspace()

          if (!workspace) {
            if (app === 'mobile') {
              return throwError(() => new HttpErrorResponse({ status: 0, statusText: 'Workspace unavailable' }))
            }

            this.logout(true, null, null, { message: `Account Service failed` })

            return EMPTY
          }

          const subdomain: Subdomain = this.browserService.isCustomSubdomain()
            ? { name: this.browserService.getSubdomain(), isDefault: false }
            : workspace.getDefaultSubdomain(this)

          return this.switchWorkspace(workspace.id, subdomain)
        }),
        map(authResponse => {
          const accountState = this.accountQuery.getAccount()
          const account = new Account(accountState.email, authResponse.expires_in)

          this.accountStore.updateAccount({
            email: account.email,
            refreshToken: accountState.refreshToken,
            accessToken: authResponse.access_token,
            refreshAt: account.refreshAt
          })

          if (this.reloadAfterSessionRefresh) {
            if (this.signalStoreService.isStorageInit()) {
              race(this.signalStoreService.selectOnStorePersisted('account'), timer(DEFAULT_PERSIST_DELAY))
                .pipe(take(1))
                .subscribe(() => {
                  window.location.replace(currentUrl)
                })
            } else {
              const mobileDelay = app === 'web' ? 0 : 1000

              setTimeout(() => window.location.replace(currentUrl), DEFAULT_PERSIST_DELAY + mobileDelay)
            }
          }
        }),
        catchError((error: HttpErrorResponse) => {
          this.appStore.setRefreshSessionError({
            message: 'Refresh session failed',
            error,
            traceId: error.headers?.get?.('trace-id'),
            date: new Date()
          })
          this.logService.logMessage(Level.warn, 'Refresh session failed', {
            status: error.status,
            traceId: error.headers?.get?.('trace-id') ?? undefined
          })

          if (app === 'web' || error.status === 400 || error.status === 401) {
            this.logout(true, null, null, {
              message: `Account Service failed with error ${error.status}`,
              error
            })
          }

          if (app === 'web') {
            return EMPTY
          }

          return throwError(() => error)
        })
      )
    }).pipe(
      tap({
        error: () => this.finishSessionRefresh(request$),
        complete: () => this.finishSessionRefresh(request$)
      }),
      finalize(() => this.finishSessionRefresh(request$)),
      // Keep one attempt alive across callers and retain its result, including failures.
      share({
        connector: () => new ReplaySubject<AuthResponse | void>(1),
        resetOnError: false,
        resetOnComplete: false,
        resetOnRefCountZero: false
      })
    )

    this.refreshSessionRequest$ = request$

    return request$
  }

  /**
   * Clears a finished refresh before callers can start another one.
   * @param {Observable<AuthResponse | void>} request The attempt that finished.
   */
  private finishSessionRefresh(request: Observable<AuthResponse | void>): void {
    if (this.refreshSessionRequest$ !== request) {
      return
    }

    this.refreshSessionRequest$ = null
    this.reloadAfterSessionRefresh = false
    this.refreshingSession$.next(false)
  }

  /**
   * Makes an API call to validate the credentials
   * @param {string} email
   * @param {string} password
   * @returns {Observable<void>}
   */
  public validate(email: string, password: string): Observable<void> {
    this.email = email
    return this.apiClient.post<void>(
      `${this.url}${this.loginEndpoint}`,
      this.getBody({
        nonce: encrypt(clientSecret, JSON.stringify({ validate: true, date: new Date() })),
        username: email,
        password,
        validate_only: 'true'
      })
    )
  }

  /**
   * Makes an API call to request a password reset
   * @param {string} email
   * @returns {Observable<object>}
   */
  forgotPassword(email: string): Observable<object> {
    this.email = email
    return this.apiClient.post<object>(`${this.url}forgotPassword`, {
      email
    })
  }

  /**
   * Makes an API call to reset the password
   * If the password is reset successfully, it will login
   * @param {string} password
   * @param {string} email
   * @param {string} code
   * @param {string} workspaceId
   * @param {Subdomain} subdomain
   * @returns {Observable<Observable<AuthResponse>>}
   */
  resetPassword(
    password: string,
    email: string,
    code: string,
    workspaceId: string,
    subdomain: Subdomain
  ): Observable<Observable<AuthResponse>> {
    return this.apiClient
      .post<string>(`${this.url}resetPassword`, {
        password,
        email,
        code
      })
      .pipe(
        map(() => {
          return this.login(email, password, workspaceId, subdomain)
        })
      )
  }

  /**
   * Logout the user, saving some login data and cleaning the storage
   * @param {boolean} [unauthorized=false] Logout caused by unauthorized
   * @param {boolean} [globalRedirect=false] Redirect to global login page after logout
   * @param {boolean} [workspaceDeleted=false]
   * @param {{ level?: 'INFO' | 'WARN' | 'ERROR' | 'FATAL'; message?: string; error?: Error | HttpErrorResponse; }} logInfo
   *   Sends a log with the provided log info
   * @param {string} logInfo.level Log severity.
   * @param {string} logInfo.message Logout reason.
   * @param {Error | HttpErrorResponse} logInfo.error Error that caused logout.
   * @param {boolean} [suppressLogoutRequestError=false]
   */
  logout(
    unauthorized = false,
    globalRedirect = false,
    workspaceDeleted = false,
    logInfo?: {
      level?: 'INFO' | 'WARN' | 'ERROR' | 'FATAL'
      message?: string
      error?: Error | HttpErrorResponse
    },
    suppressLogoutRequestError = false
  ): void {
    if (!globalRedirect && app === 'web') {
      this.getLoginData(unauthorized, suppressLogoutRequestError)
    } else {
      // If globalRedirect is true, loginData is not needed
      this.requestLogout(unauthorized, null, globalRedirect, workspaceDeleted, suppressLogoutRequestError)
    }

    if (logInfo) {
      this.appStore.setLogoutReason({
        message: logInfo.message,
        error: logInfo.error,
        traceId: logInfo.error instanceof HttpErrorResponse ? logInfo.error?.headers?.get?.('trace-id') : undefined,
        date: new Date()
      })

      const level = levelMap[logInfo.level] || Level.info
      // Log only diagnostic fields. The full error contains the workspace URL and response body.
      const metaData = logInfo.error
        ? {
            errorName: logInfo.error.name,
            status: logInfo.error instanceof HttpErrorResponse ? logInfo.error.status : undefined,
            traceId:
              logInfo.error instanceof HttpErrorResponse
                ? (logInfo.error.headers?.get?.('trace-id') ?? undefined)
                : undefined
          }
        : {}

      this.logService.logMessage(level, logInfo.message, metaData)
    }
  }

  /**
   * Stops local authentication and completes logout even if the server request fails.
   * @param {boolean} unauthorized
   * @param {LoginData} loginData
   * @param {boolean} [globalRedirect=false]
   * @param {boolean} [workspaceDeleted=false]
   * @param {boolean} [suppressLogoutRequestError=false]
   */
  requestLogout(
    unauthorized: boolean,
    loginData: LoginData,
    globalRedirect = false,
    workspaceDeleted = false,
    suppressLogoutRequestError = false
  ) {
    if (unauthorized && !workspaceDeleted) {
      this.redirectUrl = this._router.url
    }

    const refreshToken = this.accountQuery.getAccount().refreshToken

    // Stop authentication recovery before a blocked logout can trigger another refresh.
    this.clearStoreAfterNavigation = !workspaceDeleted
    this.clearAccountState()
    this.accountStore.reset()

    this.apiClient
      .post(`${this.url}logout`, refreshToken ? { refresh_token: refreshToken } : {})
      .pipe(
        finalize(() => {
          this.apiCacheService.invalidate({ all: true })
          this.subscriptions.forEach(s => s.unsubscribe())

          this.removeSubdomainCookie()

          if (loginData?.email) {
            localStorageHelper.setItem('loginData', loginData)
          }

          if (workspaceDeleted) {
            this.clearStore()
          } else {
            const byeUrl = this.appQuery.getIsInMSTeams() ? '/msteamslogin' : '/bye'

            if (!globalRedirect || environment === 'local') {
              const redirect = app === 'web' ? byeUrl : '/onboarding'

              this.clearStoreAfterNavigation = true
              this._router.navigate([redirect, unauthorized ? { unauthorized } : {}])
            } else {
              this.clearStoreAfterNavigation = true
              window.location.replace(`https://app.${this.hostname}${byeUrl}`)
            }
          }

          // clear tracking stuff
          this.trackingService.clear()
        })
      )
      .subscribe({
        error: (error: unknown) => {
          if (unauthorized || suppressLogoutRequestError) {
            this.logService.logMessage(Level.warn, 'Logout request failed', {
              status: error instanceof HttpErrorResponse ? error.status : undefined
            })

            return
          }

          throw error
        }
      })
  }

  /**
   * Clears all stores.
   */
  clearStore(): void {
    this.signalStoreService.resetStores({
      exclude: ['app', 'logs', 'workspace', 'featureFlag']
    })

    this.clearAccountState()
  }

  /**
   * Clears all stores and emits when persisted store cleanup has completed.
   * @returns {Observable<boolean>} Whether persisted workspace state was deleted successfully.
   */
  private clearStoreAndWait(): Observable<boolean> {
    const storesReset$ = this.signalStoreService.resetStoresAndWait({
      exclude: ['app', 'logs', 'workspace', 'featureFlag']
    })

    this.clearAccountState()

    return storesReset$
  }

  /**
   * Clears account state after stores are reset.
   */
  private clearAccountState(): void {
    this.appStore.update({ isLoggedIn: false, lastSyncDate: null })
    this.accountFetched = false

    if (app === 'mobile') {
      localStorageHelper.removeItem('awState')
    }
  }

  /**
   * Gets the login data to be stored and used in login page
   * @param {boolean} unauthorized
   * @param {boolean} [suppressLogoutRequestError=false]
   */
  getLoginData(unauthorized: boolean, suppressLogoutRequestError = false): void {
    // Get user information to display it in login page
    const loginData = new LoginData()
    const account = this.accountQuery.queryAccountState()

    loginData.email = account().email

    const user = this.userQuery.getCurrentUser()

    if (user) {
      loginData.name = user.fullName
      loginData.initials = user.initials
    }

    const image$ = user?.hasImage && !unauthorized ? this.getLoginImage(user) : of(null)

    image$.subscribe(image => {
      if (image !== null) {
        loginData.image = image
      }

      this.requestLogout(unauthorized, loginData, false, false, suppressLogoutRequestError)
    })
  }

  /**
   * Gets the login avatar within the logout deadline.
   * @param {User} user
   * @returns {Observable<string | null>}
   */
  private getLoginImage(user: User): Observable<string | null> {
    return this.getUserBlobImage(user).pipe(
      switchMap(blob => this.getImageBase64FromBlob(blob)),
      map(event => (event.currentTarget as FileReader).result as string),
      timeout(LOGIN_IMAGE_TIMEOUT_MS),
      catchError((error: unknown) => this.skipLoginImage(error))
    )
  }

  /**
   * Logs an unavailable login avatar and continues logout without it.
   * @param {unknown} error
   * @returns {Observable<null>}
   */
  private skipLoginImage(error: unknown): Observable<null> {
    const errorName = error && typeof error === 'object' && 'name' in error ? error.name : undefined

    this.logService.logMessage(Level.warn, 'Logout skipped login avatar', {
      reason: error instanceof TimeoutError ? 'timeout' : 'error',
      errorName: typeof errorName === 'string' ? errorName : undefined
    })

    return of(null)
  }

  /**
   * Gets the user image blob
   * @param {User} user
   * @param {number} [width=100]
   * @param {number} [height=100]
   * @returns {Observable<Blob>}
   */
  getUserBlobImage(user: User, width = 100, height = 100): Observable<Blob> {
    return this.apiClient.get<Blob>(user.profileImage(width, height), {
      responseType: 'blob'
    })
  }

  /**
   * Gets the image encoded in base64 from a blob
   * @param {Blob} image
   * @returns {Observable<Event>}
   */
  getImageBase64FromBlob(image: Blob): Observable<Event> {
    return new Observable<Event>(subscriber => {
      const reader = new FileReader()
      const onLoad = (event: Event): void => {
        subscriber.next(event)
        subscriber.complete()
      }
      const onError = (): void => subscriber.error(reader.error ?? new Error('FileReader error'))
      const onAbort = (): void => subscriber.error(reader.error ?? new DOMException('File read aborted', 'AbortError'))

      reader.addEventListener('load', onLoad)
      reader.addEventListener('error', onError)
      reader.addEventListener('abort', onAbort)
      reader.readAsDataURL(image)

      return () => {
        reader.removeEventListener('load', onLoad)
        reader.removeEventListener('error', onError)
        reader.removeEventListener('abort', onAbort)

        if (reader.readyState === FileReader.LOADING) {
          reader.abort()
        }
      }
    })
  }

  /**
   * Makes an API call to check if the account already exists
   * @param {string} email
   * @returns {Observable<ValidatedAccount>}
   */
  checkEmail(email: string): Observable<ValidatedAccount> {
    const obfuscatedEmail = rotateCharacters(email)
    const base64Email = btoa(obfuscatedEmail)
    const validateAccountBody = {
      value: base64Email
    }

    return this.apiClient.post<ValidateAccount>(`${this.url}validate`, validateAccountBody).pipe(
      map(response => {
        const numberPart = response.value.substring(0, response.value.length - 1)
        const signPart = response.value.substring(response.value.length - 1)
        const exists = parseInt(numberPart, 10) % 2 == 0
        const passwordIsAutoGenerated = signPart === '#'

        return {
          exists: exists,
          passwordIsAutoGenerated: passwordIsAutoGenerated
        }
      })
    )
  }

  /**
   * Makes an API call to check if the email is from a free domain
   * @param {string} email
   * @returns {Observable<boolean>}
   */
  checkEmailDomain(email: string): Observable<boolean> {
    return this.apiClient.post<boolean>(`${this.url}isfreemail`, { email })
  }

  /**
   * Encode a JSON as URL query params (param1=value1&param2=value2
   * @param {object} json The JSON to be encoded
   * @returns {string} query - The URL query
   */
  private getBody(json: object): string {
    let query = ''

    for (const key in json) {
      if (json.hasOwnProperty(key) && json[key]) {
        query += encodeURIComponent(key) + '=' + encodeURIComponent(json[key]) + '&'
      }
    }

    return query.slice(0, -1)
  }

  /**
   * Set the redirect URL used when a user logs in after attempted to access with no credentials
   * @param {string} url
   */
  setRedirectUrl(url: string): void {
    // URLs that are part of the Auth module, no need to redirect there
    const exceptUrl = [
      'login',
      'forgot-password',
      'reset-password',
      'set-password',
      'signup',
      'imprint',
      'bye',
      'no-workspace'
    ]

    if (url !== '/' && !exceptUrl.some(string => url.indexOf(string) >= 0)) {
      this.redirectUrl = url
    } else {
      this.redirectUrl = void 0
    }
  }

  /**
   * Get the redirect URL used when a user logs in
   * @returns {string | undefined}
   */
  getRedirectUrl(): string | undefined {
    return this.redirectUrl
  }

  /**
   * Set a temporary credential to hold login information to proceed to
   * create a new workspace (user has no workspace)
   * @param {string} email
   * @param {string} password
   */
  setTemporaryCredentials(email: string, password: string): void {
    this.email = email
    this.password = password
  }

  /**
   * Gets the temporary credential used for workspace creation
   * (user has no workspace)
   * @returns {{ email: string; password: string; }}
   */
  getTemporaryCredentials(): { email: string; password: string } {
    return {
      email: this.email,
      password: this.password
    }
  }

  /**
   * Fetches and stores the account details for the authenticated session.
   * @returns {Observable<Account>}
   */
  public fetchAccountDetails(): Observable<Account> {
    const account = this.accountQuery.getAccount()

    return this.apiClient.get<Account>(`${this.url}me`).pipe(
      map(accountDetails => {
        this.accountFetched = true
        Object.assign(account, accountDetails)
        this.accountStore.updateAccount(account)
        setPasskeyLoginPreference(accountDetails.passwordlessEnabled)
        this.appStore.update({ language: account.language })

        return Account.fromState(account)
      })
    )
  }

  /**
   * Makes an API call to updates the user account's details
   * @param {Account} account
   * @returns {Observable<Account>}
   */
  updateAccountDetails(account: Account): Observable<Account> {
    const accountPost = {
      firstName: account.firstName,
      lastName: account.lastName,
      language: account.language,
      timezone: account.timezone,
      email: account.email
    }

    const operationId = this.accountStore.history.startOperation()
    const appStoreOperationId = this.appStore.history.startOperation()

    this.accountStore.updateAccount(account)
    this.appStore.update({ language: account.language })

    return this.apiClient.put<Account>(`${this.url}${account.id}`, accountPost).pipe(
      map(newAccount => {
        this.accountStore.updateAccount(newAccount)
        this.appStore.update({ language: account.language })
        this.accountStore.history.endOperation(operationId)
        this.appStore.history.endOperation(appStoreOperationId)

        return newAccount
      }),
      catchError(error => {
        this.accountStore.history.undo(operationId)
        this.appStore.history.undo(appStoreOperationId)

        return throwError(error)
      })
    )
  }

  /**
   * Makes an API call to updates the user account's password
   * @param {string} currentPassword
   * @param {string} newPassword
   * @returns {Observable<string>}
   */
  updatePassword(currentPassword: string, newPassword: string): Observable<string> {
    return this.apiClient
      .post<string>(`${this.url}changepassword`, {
        currentPassword,
        newPassword
      })
      .pipe(tap(() => this.requestLogout(false, null)))
  }

  /**
   * Makes an API call to check the validity of the password reset code
   * @param {string} email
   * @param {string} code
   * @returns {Observable<boolean>}
   */
  checkResetPasswordCode(email: string, code: string): Observable<boolean> {
    return this.apiClient
      .post<HttpResponse<void>>(
        `${this.url}isresetpasswordcodevalid`,
        {
          email,
          code
        },
        { observe: 'response' }
      )
      .pipe(
        map(response => {
          return response.status === 200
        })
      )
  }

  /**
   * Checks if the subdomain cookie is set, and then redirect to that subdomain
   * @param {boolean} [performRedirect=true]
   * @param {string} redirectUrl
   * @param {object} queryParams
   * @returns {boolean}
   */
  checkSubdomainCookie(performRedirect = true, redirectUrl?: string, queryParams?: object): boolean {
    // check if it is a forward from appsflyer, then check query parameter awr for redirection
    this.appsFlyerRedirect(location.href)

    const subdomain = getCookie(this.subdomainCookie)
    const actualSubdomain = this.browserService.getSubdomain()
    const customSubdomain = this.browserService.isCustomSubdomain()

    // Get the internal redirect url
    const redirect = this.getRedirectUrl() ? this.getRedirectUrl() : ''

    // If the subdomain cookie is set, is different than the current
    // and the actual subdomain is not custom, redirect to that subdomain
    if (subdomain && subdomain !== actualSubdomain && !customSubdomain && environment !== 'local') {
      if (performRedirect) {
        const isTaskDetail = this.isRedirectingToTaskDetails(redirectUrl || redirect)

        if (isTaskDetail) {
          queryParams = { ...(queryParams || {}), checkDuplicateIdentifier: 'true' }
        }

        let url = `https://${subdomain}.${this.hostname}${redirectUrl ? redirectUrl : redirect}`

        url = url.replace(/\/$/, '')

        // Preserve query params
        const queryString = queryParams ? `?${buildQueryString(queryParams)}` : location.search

        window.location.replace(queryParams ? `${url}${queryString}` : url)
      }

      return true
    }

    return false
  }

  /**
   * Sets the subdomain cookie used to redirect the user to the workspace's subdomain
   * @param {Subdomain} subdomain
   */
  private setSubdomainCookie(subdomain: Subdomain): void {
    setCookie(this.subdomainCookie, subdomain.name, {
      domain: this.hostname,
      path: '/',
      expires: addMonths(new Date(), 1)
    })
  }

  /**
   * Removes the subdomain cookie used to redirect the user to the workspace's subdomain
   */
  removeSubdomainCookie(): void {
    removeCookie(this.subdomainCookie, {
      domain: this.hostname,
      path: '/'
    })
  }

  /**
   * Handles forbidden login method (credentials and social login) in case of workspaces with SSO exclusively
   * @param {string} errorCode
   * @param {Subdomain} subdomain
   */
  private handleForbiddenLoginMethod(errorCode: string, subdomain: Subdomain): void {
    if (app === 'web') {
      window.location.replace(getForbiddenLoginMethodUrl(errorCode, subdomain.name, this.hostname))
    }
  }

  /**
   * Makes an API call to send a verification email
   * @param {string} email
   * @returns {Observable<void>}
   */
  sendVerificationEmail(email: string): Observable<void> {
    return this.apiClient.post<void>(`${this.url}/sendVerificationEmail`, { email })
  }

  /**
   * Makes an API call to verify the email and updates the account with the new email
   * When updating the user email, we need to update the account store after the email is verified,
   * since the api doesn't return the new Account model
   * @param {Account} account
   * @param {string} newEmail
   * @param {string} code
   * @returns {Observable<void>}
   */
  verifyNewEmail(account: Account, newEmail: string, code: string): Observable<void> {
    return this.verifyEmail(account.email, code).pipe(
      tap(() => {
        const updatedAccount = Object.assign(account, { email: newEmail, emailConfirmed: true })
        this.accountStore.updateAccount(updatedAccount)
      })
    )
  }

  /**
   * Makes an API call to verify the email manually
   * @param {string} email
   * @param {string} code
   * @returns {Observable<void>}
   */
  verifyEmail(email: string, code: string): Observable<void> {
    return this.apiClient.post<void>(`${this.url}/verifyEmail`, { email: email, code })
  }

  /**
   * Fetches the business domains verified and available for same-domain signup
   * @param {string} workspaceId
   * @returns {Observable<string[]>}
   */
  getConfirmedBusinessDomains(workspaceId: string): Observable<string[]> {
    return this.apiClient.get<string[]>(`${this.url}/byworkspaceid/${workspaceId}/confirmedbusinessdomains`)
  }

  /**
   * Checks if the redirect URL is a task detail page matching the format tasks/{identifier}/details
   * In this case, we need to add a query parameter to check for duplicate identifiers
   * This is then handled by the TaskDeepLink guard
   * @param {string} redirectUrl
   * @returns {boolean}
   */
  isRedirectingToTaskDetails(redirectUrl: string): boolean {
    const taskDetailGeneralPattern = /tasks\/[^/]+\/details/
    const isTaskDetail = taskDetailGeneralPattern.test(redirectUrl)
    const taskDetailGuidPattern = new RegExp(`tasks/${guidRegexString}/details`)
    const hasGuid = taskDetailGuidPattern.test(redirectUrl)

    return isTaskDetail && !hasGuid
  }
}
